// Grouping a Captain's Log into trips: which entries belong to one trip, and
// what that trip adds up to.
//
// Which entries agree is `entryTripKey`'s question, not this module's -- the id
// in the entry's `fields` blob files it, and the type's own trip field
// (`summary.tripKey` -- `tripName` on a Captain's Log, absent on the Pilot's
// Logbook and on Maintenance) supplies both the label and the legacy fallback
// for entries written before ids existed. See TripIdentity.mjs.
//
// There is still no TaskPogoTrip row behind a logged trip and no foreign key.
// What changed is that the NAME stopped being the key: several unrelated day
// cruises all called "Day" used to be one trip with a dozen entries, because a
// string comparison was the only thing deciding.
//
// The key rule is deliberately the same one summarizeLogbook's per-year Trips
// count uses, so the per-trip page and the summary card can never disagree
// about how many trips a year held.

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import {
  logbookTypeById,
  readEntryField,
  parseEntryFields,
  summarizeLogbook,
  tripNameKey,
  entryTripKey,
  entryYear,
} from './LogbookTypes.mjs';
import { isNamedGroupKey } from './TripIdentity.mjs';
import { sortTripsAscending } from './TripFields.mjs';
import { tripGroupDateRange } from './TripGroups.mjs';

dayjs.extend(utc);

// Both defined next to summarizeLogbook rather than here, so the card's trip
// COUNT and this module's trip GROUPING are literally the same rule.
// Re-exported because this is the module the trip rules read from.
export { tripNameKey, entryTripKey };

// The type's trip field, or null for a type that has no notion of one (the
// Pilot's Logbook counts flights, Maintenance counts nothing). This single
// expression is the whole "which logbooks get a per-trip page" rule -- don't
// re-derive it from type ids at a call site.
export function tripFieldForType(typeId) {
  const type = logbookTypeById(typeId);
  const key = type.summary?.tripKey;
  if (!key) return null;
  return type.fields.find((field) => field.key === key) || null;
}

// The type's hours field, or null for a type that counts none (Maintenance).
// The same expression summarizeLogbook uses to total a log, exported for a
// list that mixes types -- the fleet Trips card shows a Captain's `hours` and
// a Pilot's `pic` in one Hours column, and must not guess the key per row.
export function hoursFieldForType(typeId) {
  const type = logbookTypeById(typeId);
  const key = type.summary?.hoursKey;
  if (!key) return null;
  return type.fields.find((field) => field.key === key) || null;
}

// One entry's trip name, as stored (not normalized) -- for headings and links.
export function tripNameOf(entry, typeId) {
  const field = tripFieldForType(typeId);
  if (!field) return '';
  const value = readEntryField(entry, field, parseEntryFields(entry));
  return value == null ? '' : String(value);
}

// The entries belonging to one trip, in the order given.
//
// `key` is an entryTripKey -- 'g:<id>' for a trip with an identity, 'n:<name>'
// for a legacy name-keyed one. Only a key other entries can JOIN selects
// anything: an 'e:' key belongs to one unfiled entry and is not a trip, and
// neither is a blank or malformed segment off a URL.
export function entriesForTripKey(entries, key, typeId) {
  if (!isNamedGroupKey(key)) return [];
  if (!tripFieldForType(typeId)) return [];
  return (entries || []).filter((entry) => entryTripKey(entry, typeId) === key);
}

// What one trip adds up to: how many entries, when it ran, and its totals.
//
// All three totals delegate to summarizeLogbook rather than being re-derived,
// so the `includeInSummary` rule, the entry count and the measured-not-stored
// distance are each applied in exactly one place. That is what keeps this
// page's totals line, the summary card's year row and the trips table's
// Distance column from ever disagreeing -- the distance loop used to live here
// as well, and two implementations of one sum is the drift that arrangement
// exists to prevent.
//
// Only the date SPAN is computed here: a span is a property of a trip, and the
// summary card is per-year and renders nothing from it.
//
// Maintenance (no `summary` descriptor) reports no distance at all rather than
// whatever a hand-built `fields.geoRoute` blob happened to measure. It is
// `supportsRoute: false`, has no route field in its registry, and
// TripToLogbook writes `geoRoute` only onto trip types, so nothing real is
// lost.
export function summarizeTripEntries(entries, typeId) {
  const rows = entries || [];
  const summary = summarizeLogbook(rows, typeId);

  let startDate = null;
  let endDate = null;

  for (const entry of rows) {
    // Parsed in UTC for the same reason summarizeLogbook does: a date stored
    // as UTC midnight would otherwise shift a day in the Americas.
    const date = dayjs.utc(entry?.date);
    if (date.isValid()) {
      const iso = date.toISOString();
      if (startDate === null || iso < startDate) startDate = iso;
      if (endDate === null || iso > endDate) endDate = iso;
    }
  }

  return {
    // Every row handed in, dated or not -- which is exactly what
    // summarizeLogbook's totalEntries counts.
    count: summary ? summary.totalEntries : rows.length,
    startDate,
    endDate,
    totalHours: summary ? summary.totalHours : 0,
    // null rather than 0 when nothing measured, so a trip with no routes says
    // nothing about its distance instead of claiming it covered none.
    totalDistanceNm: summary ? summary.totalDistanceNm : null,
  };
}

// The entries whose date falls in `year`, in the order given.
//
// An entry with a missing or unparseable date belongs to no year at all --
// exactly the rule summarizeLogbook's per-year breakdown applies. (Its hours
// still reach the card's grand total, which is why that footer can exceed the
// sum of the year rows. That is correct, not a rounding bug.)
//
// A non-integer year yields nothing rather than everything: a garbage URL
// segment must show an empty year, not the whole logbook.
export function entriesForYear(entries, year) {
  const y = Number(year);
  if (!Number.isInteger(y)) return [];
  return (entries || []).filter((entry) => entryYear(entry) === y);
}

// One row per trip for a year, newest-first:
//
//   { key, name, entries, summary }
//     key     - the entryTripKey: 'g:<trip id>' for a trip with an identity,
//               'n:<normalized name>' for a legacy name-keyed one, or
//               'e:<entry id>' for an entry nobody filed under a trip (the same
//               shapes tripGroupKey uses for legs)
//     name    - how the trip was FIRST spelled: the name on its earliest
//               entry, trimmed. The rows group on the normalized key, so a
//               trip typed three ways has to be shown as something, and the
//               spelling it was given when it started beats whichever entry
//               the query happened to return first. '' for an unfiled entry --
//               the "(no trip name)" wording belongs to the page, not here.
//     entries - that row's entries, oldest-first
//     summary - summarizeTripEntries(entries, typeId)
//
// Rows are the year's OWN SLICE of a trip: a trip crossing New Year's Eve gets
// a row in both years, each holding only that year's entries. That is what
// makes the rows' hours add up to the summary card's year row, and the row
// count equal that row's Trips count. Following a row then shows the WHOLE
// trip -- which is exactly why the page must hand over no entries when it does.
//
// A type with no trip field (Pilot's, Maintenance) yields one row per entry,
// which is the only honest thing a deep link to such a year can show.
export function tripsInYear(entries, typeId, year) {
  return tripsInLogbook(entriesForYear(entries, year), typeId);
}

// The same rows over WHOLE logbook rather than one year of it, which is what
// "the trip before this one" has to be asked against -- a trip either side of
// New Year's Eve is still the neighbour.
//
// tripsInYear is this function with the year filter in front of it, so the two
// cannot drift about what a trip row is. One thing does NOT carry over: that
// caller's entries all have parseable dates by construction, so its sort needed
// no null branch. Here an entry with a blank or unreadable date is included,
// its row's `summary.endDate` is null, and `Date.parse(null)` is NaN -- which
// would leave the sort order unspecified for the whole list rather than just
// for that row. Undated rows are ordered last explicitly.
export function tripsInLogbook(entries, typeId) {
  const rows = new Map();

  for (const entry of entries || []) {
    // An unfiled entry keys as itself ('e:<id>') rather than joining a shared
    // "no name" bucket: two unrelated entries have nothing to do with each
    // other, and the row has to lead somewhere -- to that one entry's page.
    const rowKey = entryTripKey(entry, typeId);
    const named = isNamedGroupKey(rowKey);
    if (!rows.has(rowKey)) rows.set(rowKey, { key: rowKey, named, entries: [] });
    rows.get(rowKey).entries.push(entry);
  }

  return [...rows.values()]
    .map(({ key, named, entries: rowEntries }) => {
      // Oldest-first within a row, the way LogbookTrip reads a trip -- and
      // reversed first for the same reason it reverses: both sources come back
      // newest-first, and mirroring them is the only thing that orders entries
      // sharing one date. The sort is stable, so it leaves those ties alone.
      const ordered = sortTripsAscending([...rowEntries].reverse());
      return {
        key,
        name: named ? tripNameOf(ordered[0], typeId).trim() : '',
        entries: ordered,
        summary: summarizeTripEntries(ordered, typeId),
      };
    })
    // Newest-first, matching the summary card's years and the logbook list the
    // user just came from. A row with no readable date at all sorts last rather
    // than comparing NaN -- see the header.
    .sort((a, b) => {
      const ea = Date.parse(a.summary.endDate);
      const eb = Date.parse(b.summary.endDate);
      const na = Number.isNaN(ea);
      const nb = Number.isNaN(eb);
      if (na || nb) return na && nb ? 0 : (na ? 1 : -1);
      return eb - ea;
    });
}

// Show integers as-is and everything else to one decimal, so summed floats
// (e.g. 0.1 + 0.2) don't render with noise.
export function displayHours(hours) {
  const n = Number(hours) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// '' rather than '0.0' for an unmeasured distance: a trip whose entries carry
// no routes says nothing about how far it went, it didn't go nowhere.
export function displayNm(nm) {
  if (nm == null) return '';
  return `${(Math.round(nm * 10) / 10).toFixed(1)} nm`;
}

// The Summary card's formatters: a whole log or a whole year read to the
// nearest hour and the nearest mile. Deliberately coarser than displayHours /
// displayNm, which the per-trip columns, the totals lines and the per-record
// rows keep -- one decimal means something for a single passage and nothing
// for a season. Same contracts otherwise: null/NaN hours are '0', an
// unmeasured distance is '' (a blank cell, never '0 nm'), and Math.round
// carries a half up (10.5 -> 11).
export function displayWholeHours(hours) {
  return String(Math.round(Number(hours) || 0));
}

export function displayWholeNm(nm) {
  if (nm == null) return '';
  return `${Math.round(nm)} nm`;
}

// "Aug 1 - Aug 12, 2026 · 4 entries · 22.5 hours · 118.4 nm", as parts. Parts
// with nothing to say drop out rather than printing a zero. Shared by the
// per-trip and per-year pages so the two totals lines can't drift, and using
// the same displayHours the summary card's rows do.
export function tripSummaryParts(summary) {
  const parts = [];
  const range = tripGroupDateRange(summary);
  if (range) parts.push(range);
  parts.push(`${summary.count} ${summary.count === 1 ? 'entry' : 'entries'}`);
  if (summary.totalHours > 0) parts.push(`${displayHours(summary.totalHours)} hours`);
  const nm = displayNm(summary.totalDistanceNm);
  if (nm) parts.push(nm);
  return parts;
}
